import {Button} from '@wandb/weave/components/Button';
import * as Dialog from '@wandb/weave/components/Dialog';
import * as Tooltip from '@wandb/weave/components/RadixTooltip';
import React, {useCallback, useState} from 'react';

import {parseBucketsFromSemanticLegendSettings} from '../../../components/WorkspaceDrawer/Settings/semanticLegend/utils/parseBuckets';
import {
  RunColorOptions,
  YAxisValues,
} from '../../../components/WorkspaceDrawer/Settings/types';
import {
  SemanticLegendsQueryDocument,
  useNamedWorkspaceUpdateViewFieldsLazyQuery,
  useUpdateSemanticLegendMutation,
} from '../../../generated/graphql';
import {usePanelsSharedRefsContext} from '../contexts/PanelsSharedRefsContext';
import {
  useViewBarActionContext,
  useViewBarContext,
} from '../NamedWorkspaceViewBarContext';
import {
  getDisabledTooltipContent,
  parseAndTrackNamedWorkspaces,
} from '../utils';
import {
  getLocalStorageShouldDisableSaveViewModal,
  getShouldShowSaveWarning,
} from './modals/SaveViewModalContent';

export const ViewBarButton = () => {
  const {workspaceType, viewer, shouldRevokeWritePerms} = useViewBarContext();
  const {copy, save} = useSaveAndCopyActions();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  // there is no action if you're not logged in
  if (workspaceType === 'unknown' || viewer?.username == null) {
    return null;
  }

  return (
    <div className="hidden gap-8 xl:contents">
      {save.isDisplayed && (
        <Tooltip.Root open={isTooltipOpen} onOpenChange={setIsTooltipOpen}>
          <Tooltip.Trigger>
            <Dialog.Trigger asChild>
              <Button
                data-test="nw-save-view"
                disabled={save.isDisabled}
                variant="secondary"
                onClick={save.fn}>
                Save view
              </Button>
            </Dialog.Trigger>
          </Tooltip.Trigger>
          {shouldRevokeWritePerms && (
            <Tooltip.Portal>
              <Tooltip.Content side="bottom" avoidCollisions>
                <p className="ml-8 w-[18rem] font-normal">
                  {getDisabledTooltipContent(viewer)}
                </p>
              </Tooltip.Content>
            </Tooltip.Portal>
          )}
        </Tooltip.Root>
      )}
      {/* Don't show this button if personal workspace belongs to the user */}
      {copy.isDisplayed && (
        <Button
          data-test="nw-copy-to-personal"
          variant="primary"
          onClick={copy.fn}>
          Copy to my workspace
        </Button>
      )}
    </div>
  );
};

export const useSaveAndCopyActions = () => {
  const {
    workspaceType,
    viewer,
    copyToPersonalWorkspace,
    saveView,
    currentView,
    shouldRevokeWritePerms,
    undoWorkspaceAction,
    personalWorkspaces,
  } = useViewBarContext();
  const {setModalDetails} = useViewBarActionContext();
  const {semanticLegendSettings} = usePanelsSharedRefsContext();
  const [updateSemanticLegendSettings] = useUpdateSemanticLegendMutation();
  const viewerUsername = viewer?.username;

  // Go grab the latest updatedValue from db to determine if there is a potential conflict
  const [getUpdateViewFields, {loading, data: latestData}] =
    useNamedWorkspaceUpdateViewFieldsLazyQuery({
      fetchPolicy: 'network-only',
    });

  const copy = useCallback(() => {
    parseAndTrackNamedWorkspaces({
      action: 'Copy to personal workspace',
      location: 'view bar',
      currentView,
      viewer,
    });
    copyToPersonalWorkspace?.();

    // if we have a semantic legend, copy it to the personal workspace
    if (
      semanticLegendSettings &&
      semanticLegendSettings.runColorOptions ===
        RunColorOptions.MetricBasedColors
    ) {
      const {customColors, bucketUpperBounds} =
        parseBucketsFromSemanticLegendSettings(semanticLegendSettings);
      const personalWorkspace = personalWorkspaces[viewerUsername ?? ''];
      if (personalWorkspace) {
        updateSemanticLegendSettings({
          variables: {
            id: personalWorkspace.id ?? '',
            currentMetric: semanticLegendSettings?.metric ?? '',
            metricName: semanticLegendSettings?.metric ?? '',
            yValue: semanticLegendSettings?.yAxisValue ?? YAxisValues.Min,
            customColors: customColors?.map(color => color ?? ''),
            bucketUpperBounds: bucketUpperBounds?.map(bucket => bucket ?? 0),
            excludedRunColor: semanticLegendSettings?.excludedRunColor,
          },
          refetchQueries: [
            {
              query: SemanticLegendsQueryDocument,
              variables: {id: personalWorkspace.id ?? ''},
            },
          ],
        });
      }
    }
  }, [
    copyToPersonalWorkspace,
    currentView,
    viewer,
    semanticLegendSettings,
    updateSemanticLegendSettings,
    personalWorkspaces,
    viewerUsername,
  ]);

  const save = useCallback(() => {
    if (currentView?.id == null) {
      return;
    }

    getUpdateViewFields({variables: {id: currentView.id}});
    const shouldShowSaveWarning = getShouldShowSaveWarning(
      latestData,
      currentView
    );

    if (shouldShowSaveWarning || !getLocalStorageShouldDisableSaveViewModal()) {
      // Always show the save modal if there's a save conflict
      setModalDetails({
        type: 'save view',
      });
      return;
    }
    parseAndTrackNamedWorkspaces({
      action: 'Save view',
      location: 'view bar',
      currentView,
      viewer,
    });
    saveView?.();
  }, [
    currentView,
    getUpdateViewFields,
    latestData,
    saveView,
    setModalDetails,
    viewer,
  ]);

  const isCopyAllowed =
    copyToPersonalWorkspace != null &&
    (workspaceType !== 'personal' ||
      (workspaceType === 'personal' &&
        viewerUsername !== currentView?.user.username));

  const isSavedAllowed = workspaceType !== 'personal';
  const isSaveDisabled =
    saveView == null ||
    loading ||
    undoWorkspaceAction == null ||
    shouldRevokeWritePerms;

  return {
    copy: {
      fn: copy,
      isDisplayed: isCopyAllowed,
    },
    save: {
      fn: save,
      isDisplayed: isSavedAllowed,
      isDisabled: isSaveDisabled,
    },
  };
};
